import Joi from "@hapi/joi";
import { addDays, isAfter, startOfDay } from "date-fns";
import countries from "../assets/countries.json";
import clsx, { ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const countryDialingCodes: Record<string, string> = Object.keys(
  countries
).reduce((acc, key) => {
  acc[countries[key as keyof typeof countries].calling_code] = key;
  return acc;
}, {} as Record<string, string>);

export const getCappedAmount = (amount: number) => {
  const gates = [
    1000, 5000, 10000, 20000, 50000, 100000, 150000, 200000, 300000, 500000,
    1000000, 5000000, 10000000,
  ];

  let lastGate = 0;

  for (const gate of gates) {
    if (amount < gate) {
      return lastGate;
    }

    lastGate = gate;
  }

  return lastGate;
};

export const validCountries = [
  {
    name: "Nigeria",
    code: "NG",
    dialCode: "+234",
    flag: "🇳🇬",
  },
  {
    name: "Kenya",
    code: "KE",
    dialCode: "+254",
    flag: "🇰🇪",
  },
  {
    name: "Uganda",
    code: "UG",
    dialCode: "+256",
    flag: "🇺🇬",
  },
];

export const removePhoneNumberPrefix = (phoneNumber: string) => {
  // iterate over each dialing code
  for (const dialingCode of Object.keys(countryDialingCodes)) {
    // constructing the regular expression dynamically to match the dialing code for each country
    const regex = new RegExp(`^\\${dialingCode}`);
    // replace the prefix if it matches the dialing code
    phoneNumber = phoneNumber.replace(regex, "");
  }
  return phoneNumber;
};

export const extractPhoneNumberPrefix = (phoneNumber?: string) => {
  if (!phoneNumber) return undefined;
  for (const dialingCode of Object.keys(countryDialingCodes)) {
    const regex = new RegExp(`^\\${dialingCode}`);
    if (regex.test(phoneNumber)) {
      // Return the matched dialing code
      return dialingCode;
    }
  }

  return undefined; // Return undefined if no match is found
};

export const hasValidCharacters = (phoneNumberString: string) => {
  return /^[0-9]+$/.test(phoneNumberString);
};

export const cleanPhoneNumber = (phoneNumberString: string) => {
  let cleaned = phoneNumberString.trim();
  // remove braces, dashes and spaces
  cleaned = cleaned.replace(/-|\s|\(|\)/g, "");

  // remove the country code
  cleaned = removePhoneNumberPrefix(phoneNumberString);

  if (cleaned.startsWith("0")) {
    cleaned = cleaned.replace(/^0/, "");
  }

  return cleaned;
};

const phoneNumberValidatorMap: Record<
  string,
  (phoneNumber: string) => boolean
> = {
  "+234": (phoneNumber: string) => {
    let trimmedNumber = phoneNumber.trim();

    const cleaned = cleanPhoneNumber(trimmedNumber);

    if (!hasValidCharacters(cleaned)) {
      return false;
    }

    if (cleaned.length && cleaned.length === 10) {
      return true;
    }

    return false;
  },

  default: (phoneNumber: string) => {
    const cleaned = cleanPhoneNumber(phoneNumber);

    if (!hasValidCharacters(cleaned)) {
      return false;
    }

    return cleaned.length >= 8 && cleaned.length <= 15;
  },
};

export const isValidIntlPhoneNumber = (phoneNumberString: string) => {
  const prefix = extractPhoneNumberPrefix(phoneNumberString);

  if (!prefix) {
    return false;
  }

  const validator =
    phoneNumberValidatorMap[prefix] || phoneNumberValidatorMap.default;

  return validator(phoneNumberString);
};

export const isValidText = (text: string) => {
  const cleaned = text.trim();

  if (cleaned.length === 0) {
    return false;
  }

  return true;
};

export const isValidPhoneNumber = (
  phoneNumberString: string,
  regex: RegExp = /^((70|71)|(80|81)|(90|91))\d{8}$/
) => {
  const cleaned = cleanPhoneNumber(phoneNumberString);

  // Check if the cleaned number is 10 digits long
  if (cleaned.length !== 10) {
    return false;
  }

  if (!regex.test(cleaned)) {
    return false;
  }

  // If it's not 10 digits long, it's not a number.
  return true;
};

export const schema = Joi.string().email({
  minDomainSegments: 2,
  tlds: { allow: false },
});

export const isValidEmail = (emailString: string) => {
  const { error } = schema.validate(emailString);

  if (error) {
    return false;
  }

  return true;
};

export const isValidHomeownerHireStartDate = (date: Date) => {
  return isAfter(date, addDays(startOfDay(new Date()), 1));
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0 });
};

export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: unknown[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const normalizeString = (str: string) => {
  const lower = str.toLowerCase().replaceAll("_", " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};